import axios from 'axios';
import { url } from '../client_config/config';

export default {
  namespaced: true,
  state: {
    userPermissions: null,
    allowedDashboardSections: null,
    availableComponents: [],
    componentsGrouped: {},
    roles: [],
    loading: false,
    error: null
  },
  mutations: {
    SET_USER_PERMISSIONS(state, permissions) {
      state.userPermissions = permissions;
    },
    SET_ALLOWED_DASHBOARD_SECTIONS(state, sections) {
      state.allowedDashboardSections = sections === undefined ? null : sections;
    },
    SET_AVAILABLE_COMPONENTS(state, { components, grouped }) {
      state.availableComponents = components;
      state.componentsGrouped = grouped;
    },
    SET_ROLES(state, roles) {
      state.roles = roles;
    },
    ADD_ROLE(state, role) {
      state.roles.push(role);
    },
    UPDATE_ROLE(state, updatedRole) {
      const index = state.roles.findIndex(r => r._id === updatedRole._id);
      if (index !== -1) {
        state.roles.splice(index, 1, updatedRole);
      }
    },
    REMOVE_ROLE(state, roleId) {
      state.roles = state.roles.filter(r => r._id !== roleId);
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    CLEAR_ERROR(state) {
      state.error = null;
    },
    RESET_STATE(state) {
      state.userPermissions = null;
      state.allowedDashboardSections = null;
      state.availableComponents = [];
      state.componentsGrouped = {};
      state.roles = [];
      state.loading = false;
      state.error = null;
    }
  },
  actions: {
    async fetchPermissions({ commit, rootState }) {
      try {
        const user = rootState.auth.user;
        if (!user) {
          return;
        }

        // Skip fetching for Super-Admin and Performer (universal roles)
        if (user.userType === 'super_admin' || user.userType === 'performer') {
          return;
        }

        // For Admin users, use _id as companyId if company is not set
        // This matches the backend behavior in server/routes/roles.js
        if (!user.company && user.userType === 'admin') {
          // Admin users can use their own _id as companyId
          const response = await axios.get(`${url}/roles/user/${user._id}/permissions`, {
            headers: {
              Authorization: `Bearer ${rootState.auth.token}`
            }
          });

          if (response.data.success) {
            const permissions = response.data.permissions || {};
            permissions.Dashboard = {
              view: true,
              create: false,
              edit: false,
              delete: false
            };
            permissions.Profile = {
              view: true,
              create: false,
              edit: true,
              delete: false
            };
            commit('SET_USER_PERMISSIONS', permissions);
            commit('SET_ALLOWED_DASHBOARD_SECTIONS', response.data.dashboardSections);
          }
          return;
        }

        // For other user types (judge, instructor, production_manager, custom types)
        // Note: user.company might be an object (populated) or just an ID
        let companyId = user.company?._id || user.company;

        // production_manager stores their company in a different field
        if (!companyId && user.productionManager?.associatedCompany) {
          companyId = user.productionManager.associatedCompany?._id || user.productionManager.associatedCompany;
        }

        // If no company ID, try to get it from companyRoles
        if (!companyId && user.companyRoles && user.companyRoles.length > 0) {
          // Use the first company role's company ID
          const firstCompanyRole = user.companyRoles[0];
          const roleCompanyId = firstCompanyRole.company?._id || firstCompanyRole.company;
          if (roleCompanyId) {
            const response = await axios.get(`${url}/roles/user/${user._id}/permissions?companyId=${roleCompanyId}`, {
              headers: {
                Authorization: `Bearer ${rootState.auth.token}`
              }
            });

            if (response.data.success) {
              commit('SET_USER_PERMISSIONS', response.data.permissions);
              commit('SET_ALLOWED_DASHBOARD_SECTIONS', response.data.dashboardSections);
            }
            return;
          }
        }

        if (!companyId) {
          // No company ID available, can't fetch permissions
          return;
        }

        const response = await axios.get(`${url}/roles/user/${user._id}/permissions`, {
          headers: {
            Authorization: `Bearer ${rootState.auth.token}`
          }
        });

        if (response.data.success) {
          const permissions = response.data.permissions || {};
          permissions.Dashboard = {
            view: true,
            create: false,
            edit: false,
            delete: false
          };
          permissions.Profile = {
            view: true,
            create: false,
            edit: true,
            delete: false
          };
          commit('SET_USER_PERMISSIONS', permissions);
          commit('SET_ALLOWED_DASHBOARD_SECTIONS', response.data.dashboardSections);
        }
      } catch (error) {
        console.error('Error fetching permissions:', error);
        commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch permissions');
      }
    },

    async fetchComponents({ commit, rootState }) {
      try {
        const response = await axios.get(`${url}/roles/components`, {
          headers: {
            Authorization: `Bearer ${rootState.auth.token}`
          }
        });

        if (response.data.success) {
          commit('SET_AVAILABLE_COMPONENTS', {
            components: response.data.components,
            grouped: response.data.grouped
          });
        }
      } catch (error) {
        console.error('Error fetching components:', error);
        commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch components');
      }
    },

    async fetchRoles({ commit, rootState }) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');

      try {
        const response = await axios.get(`${url}/roles`, {
          headers: {
            Authorization: `Bearer ${rootState.auth.token}`
          }
        });

        if (response.data.success) {
          commit('SET_ROLES', response.data.roles);
        }
      } catch (error) {
        console.error('Error fetching roles:', error);
        commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch roles');
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async createRole({ commit, rootState }, roleData) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');

      try {
        const response = await axios.post(`${url}/roles`, roleData, {
          headers: {
            Authorization: `Bearer ${rootState.auth.token}`
          }
        });

        if (response.data.success) {
          commit('ADD_ROLE', response.data.role);
          return { success: true, role: response.data.role };
        }
      } catch (error) {
        console.error('Error creating role:', error);
        const errorMessage = error.response?.data?.message || 'Failed to create role';
        commit('SET_ERROR', errorMessage);
        throw new Error(errorMessage);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async updateRole({ commit, rootState }, { roleId, roleData }) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');

      try {
        const response = await axios.put(`${url}/roles/${roleId}`, roleData, {
          headers: {
            Authorization: `Bearer ${rootState.auth.token}`
          }
        });

        if (response.data.success) {
          commit('UPDATE_ROLE', response.data.role);
          return { success: true, role: response.data.role };
        }
      } catch (error) {
        console.error('Error updating role:', error);
        const errorMessage = error.response?.data?.message || 'Failed to update role';
        commit('SET_ERROR', errorMessage);
        throw new Error(errorMessage);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async deleteRole({ commit, rootState }, roleId) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');

      try {
        const response = await axios.delete(`${url}/roles/${roleId}`, {
          headers: {
            Authorization: `Bearer ${rootState.auth.token}`
          }
        });

        if (response.data.success) {
          commit('REMOVE_ROLE', roleId);
          return { success: true };
        }
      } catch (error) {
        console.error('Error deleting role:', error);
        const errorMessage = error.response?.data?.message || 'Failed to delete role';
        commit('SET_ERROR', errorMessage);
        throw new Error(errorMessage);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async assignRoleToUser({ rootState }, { roleId, userId }) {
      try {
        const response = await axios.post(
          `${url}/roles/${roleId}/assign-user`,
          { userId },
          {
            headers: {
              Authorization: `Bearer ${rootState.auth.token}`
            }
          }
        );

        if (response.data.success) {
          return { success: true, user: response.data.user };
        }
      } catch (error) {
        console.error('Error assigning role:', error);
        const errorMessage = error.response?.data?.message || 'Failed to assign role';
        throw new Error(errorMessage);
      }
    },

    async unassignRoleFromUser({ rootState }, { roleId, userId }) {
      try {
        const response = await axios.delete(
          `${url}/roles/${roleId}/unassign-user/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${rootState.auth.token}`
            }
          }
        );

        if (response.data.success) {
          return { success: true };
        }
      } catch (error) {
        console.error('Error unassigning role:', error);
        const errorMessage = error.response?.data?.message || 'Failed to unassign role';
        throw new Error(errorMessage);
      }
    },

    resetPermissions({ commit }) {
      commit('RESET_STATE');
    }
  },
  getters: {
    hasPermission: (state) => (component, action) => {
      if (!state.userPermissions) {
        return false;
      }

      const componentPerms = state.userPermissions[component];
      if (!componentPerms) {
        return false;
      }

      return componentPerms[action] === true;
    },

    canCreate: (state, getters) => (component) => {
      return getters.hasPermission(component, 'create');
    },

    canView: (state, getters) => (component) => {
      return getters.hasPermission(component, 'view');
    },

    canEdit: (state, getters) => (component) => {
      return getters.hasPermission(component, 'edit');
    },

    canDelete: (state, getters) => (component) => {
      return getters.hasPermission(component, 'delete');
    },

    canAccess: (state, getters) => (component) => {
      return getters.canView(component);
    },

    userPermissions: (state) => state.userPermissions,
    allowedDashboardSections: (state) => state.allowedDashboardSections,
    availableComponents: (state) => state.availableComponents,
    componentsGrouped: (state) => state.componentsGrouped,
    roles: (state) => state.roles,
    loading: (state) => state.loading,
    error: (state) => state.error
  }
};
